import React, { createContext, useContext, useState } from 'react';

export interface NewsfeedSource {
  id: string;
  brand_id: string;
  name: string;
  rss_url: string;
  feed_type: 'rss' | 'atom' | 'custom_txt' | 'html';
  category_id: string | null;
  enabled: boolean;
  max_articles: number;
  fallback_image_url: string | null;
  created_at: string;
  updated_at: string;
  custom_list_marker?: string;
  user_id: string;
  // HTML scraping fields
  html_article_selector?: string | null;
  html_title_selector?: string | null;
  html_link_selector?: string | null;
  html_description_selector?: string | null;
  html_image_selector?: string | null;
  html_date_selector?: string | null;
  use_ai_extraction?: boolean;
}

interface NewsfeedSourceContextType {
  isOpen: boolean;
  editingSource: NewsfeedSource | null;
  openAddSource: () => void;
  openEditSource: (source: NewsfeedSource) => void;
  closeSourceDialog: () => void;
}

const NewsfeedSourceContext = createContext<NewsfeedSourceContextType | undefined>(undefined);

export const NewsfeedSourceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<NewsfeedSource | null>(null);

  const openAddSource = () => {
    setEditingSource(null);
    setIsOpen(true);
  };

  const openEditSource = (source: NewsfeedSource) => {
    setEditingSource(source);
    setIsOpen(true);
  };

  const closeSourceDialog = () => {
    setIsOpen(false);
    setEditingSource(null);
  };

  return (
    <NewsfeedSourceContext.Provider 
      value={{ 
        isOpen, 
        editingSource, 
        openAddSource, 
        openEditSource, 
        closeSourceDialog 
      }}
    >
      {children}
    </NewsfeedSourceContext.Provider>
  );
};

export const useNewsfeedSource = () => {
  const context = useContext(NewsfeedSourceContext);
  if (context === undefined) {
    throw new Error('useNewsfeedSource must be used within a NewsfeedSourceProvider');
  }
  return context;
};
